import { Link } from "react-router-dom";
import { SEO } from "@/components/SEO";
import { Separator } from "@/components/ui/separator";
import { getHowToSchema, getBreadcrumbSchema } from "@/lib/schema";
import { 
  ArrowRight, 
  CheckCircle2, 
  AlertTriangle, 
  TrendingUp, 
  Lightbulb, 
  DollarSign, 
  Clock, 
  Users, 
  LineChart,
  Target,
  Zap
} from "lucide-react";

const ValidateStartup = () => {
  return (
    <>
      <SEO
        title="Validate Your Startup Idea Before Building | WillTheyConvert"
        description="Don't waste time and money building products nobody wants. Our validation platform helps you test if real users will convert before writing a single line of code."
        keywords="startup validation, product-market fit, customer validation, lean startup, MVP, startup success, idea validation, pre-launch testing, market validation, fake door testing"
        canonicalUrl="/validate-startup"
        jsonLd={[
          getBreadcrumbSchema([
            { name: "Home", url: "/" },
            { name: "Validate Startup", url: "/validate-startup" },
          ]),
          getHowToSchema(
            "How to Validate Your Startup Idea",
            "A step-by-step guide to validating your startup idea before building.",
            [
              { name: "Create Your Test", text: "Set up a validation flow with your product details and pricing in minutes." },
              { name: "Drive Traffic", text: "Share your test link with your target audience via social media, ads, or email." },
              { name: "Measure Intent", text: "Track real user behavior and see who clicks the buy button." },
              { name: "Make Decisions", text: "Use conversion data to decide whether to build, pivot, or move on." },
            ]
          ),
        ]}
      />
      
      {/* Hero Section with Animation */}
      <div className="relative bg-gradient-to-b from-indigo-50/50 via-violet-50/30 to-background py-20 overflow-hidden">
        <div className="absolute inset-0 bg-grid-pattern opacity-5"></div>
        <div className="container max-w-6xl relative z-10">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
            <div className="space-y-6">
              <div className="inline-flex items-center px-3 py-1 rounded-full bg-gradient-to-r from-indigo-100 to-violet-100 text-indigo-700 text-sm font-medium">
                <Zap className="h-4 w-4 mr-1" /> Stop Building Products No One Wants
              </div>
              <h1 className="text-4xl sm:text-5xl font-bold tracking-tight leading-tight">
                <span className="bg-clip-text text-transparent bg-gradient-to-r from-indigo-600 to-violet-600">Validate</span> Your Startup Idea <span className="block mt-1">Before Wasting Resources</span>
              </h1>
              <p className="text-xl text-muted-foreground">
                91% of startups fail. The #1 reason? Building something nobody wants. Test your idea with real users <span className="font-semibold">before</span> investing time and money.
              </p>
              <div className="flex gap-4 flex-wrap">
                <Link to="/create" className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-indigo-600 to-violet-600 hover:from-indigo-700 hover:to-violet-700 text-white rounded-full text-lg font-medium shadow-md hover:shadow-lg transform hover:-translate-y-1 transition-all">
                  Create Your Free Test
                  <ArrowRight className="h-4 w-4" />
                </Link>
                <a href="#how-it-works" className="inline-flex items-center gap-2 px-6 py-3 bg-white text-indigo-600 rounded-full border-2 border-indigo-200 hover:bg-indigo-50 transition-all text-lg font-medium">
                  See How It Works
                </a>
              </div>
              <div className="flex items-center gap-4 text-sm text-muted-foreground">
                <div className="flex items-center">
                  <CheckCircle2 className="h-4 w-4 text-green-500 mr-1" />
                  No coding required
                </div>
                <div className="flex items-center">
                  <CheckCircle2 className="h-4 w-4 text-green-500 mr-1" />
                  Results in 24 hours
                </div>
                <div className="flex items-center">
                  <CheckCircle2 className="h-4 w-4 text-green-500 mr-1" />
                  Free for early adopters
                </div>
              </div>
            </div>
            <div className="relative">
              <div className="absolute -top-10 -right-10 w-40 h-40 bg-gradient-to-br from-indigo-200 to-violet-200 rounded-full blur-3xl"></div>
              <img
                src="/images/valide-startup.jpg"
                alt="Startup Validation Process"
                className="rounded-2xl shadow-xl max-w-full relative z-10 border border-gray-200"
              />
            </div>
          </div>
        </div>
      </div>
      
      {/* Stats Section */}
      <div className="bg-gradient-to-r from-indigo-600 to-violet-600 py-12">
        <div className="container max-w-6xl">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
            {[
              { number: "3x", label: "Higher success rate", icon: <TrendingUp className="h-6 w-6 text-white/80 mx-auto mb-2" /> },
              { number: "$18k", label: "Average savings", icon: <DollarSign className="h-6 w-6 text-white/80 mx-auto mb-2" /> },
              { number: "4.2 mo", label: "Time saved", icon: <Clock className="h-6 w-6 text-white/80 mx-auto mb-2" /> },
              { number: "92%", label: "Would recommend", icon: <Users className="h-6 w-6 text-white/80 mx-auto mb-2" /> }
            ].map((stat, i) => (
              <div key={i} className="p-4 text-white">
                {stat.icon}
                <p className="text-3xl font-bold">{stat.number}</p>
                <p className="text-sm text-indigo-100">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
      
      {/* Main Content */}
      <div className="container max-w-5xl py-16">
        <div className="space-y-20">
          <section className="space-y-8">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-indigo-500 to-violet-600 flex items-center justify-center">
                <Lightbulb className="h-6 w-6 text-white" />
              </div>
              <h2 className="text-3xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-indigo-600 to-violet-600">The Cost of Not Validating</h2>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div className="bg-white rounded-2xl p-8 shadow-lg border-2 border-red-200 h-full">
                <h3 className="text-xl font-bold mb-4 flex items-center">
                  <AlertTriangle className="h-5 w-5 text-red-500 mr-2" />
                  Building Without Validation
                </h3>
                <ul className="space-y-3 mb-6">
                  {[
                    "Spend 6-12 months building the wrong product",
                    "Burn through $50k-$200k in development costs",
                    "Launch to crickets and zero users",
                    "Endless pivots trying to find customers",
                    "Team burnout and founder depression",
                    "Eventually run out of money and shut down"
                  ].map((item, i) => (
                    <li key={i} className="flex items-start gap-2">
                      <div className="rounded-full h-5 w-5 bg-red-100 text-red-500 flex items-center justify-center flex-shrink-0 mt-0.5">
                        <span className="text-xs font-bold">✕</span>
                      </div>
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
                <div className="p-3 bg-red-50 rounded-md text-sm text-red-700 border border-red-100">
                  <strong>42% of startups fail</strong> because they built something no one wanted, according to CB Insights.
                </div>
              </div>
              
              <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-2xl p-8 shadow-lg border-2 border-green-300 h-full">
                <h3 className="text-xl font-bold mb-4 flex items-center">
                  <CheckCircle2 className="h-5 w-5 text-green-500 mr-2" />
                  The Validation Approach
                </h3>
                <ul className="space-y-3 mb-6">
                  {[
                    "Test your idea in just a few days",
                    "Spend less than $500 on validation",
                    "Gather real data from actual customers",
                    "Refine your idea based on feedback",
                    "Build confidence in your business model",
                    "Start building with a proven concept"
                  ].map((item, i) => (
                    <li key={i} className="flex items-start gap-2">
                      <CheckCircle2 className="h-5 w-5 text-green-500 flex-shrink-0 mt-0.5" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
                <div className="p-3 bg-green-50 rounded-md text-sm text-green-700 border border-green-100">
                  <strong>Founders who validate first</strong> are 3x more likely to build a successful startup, according to Startup Genome.
                </div>
              </div>
            </div>
          </section>
          
          {/* Success Stories Section */}
          <section className="space-y-8">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-indigo-500 to-violet-600 flex items-center justify-center">
                <Users className="h-6 w-6 text-white" />
              </div>
              <h2 className="text-3xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-indigo-600 to-violet-600">Success Stories</h2>
            </div>

            <div className="bg-gradient-to-r from-indigo-50 to-violet-50 rounded-2xl p-8 border border-indigo-200 relative overflow-hidden shadow-lg">
              <div className="absolute top-0 right-0 w-40 h-40 bg-gradient-to-br from-indigo-200 to-violet-200 rounded-full -translate-y-1/2 translate-x-1/2"></div>
              
              <div className="flex flex-col md:flex-row gap-10 items-center relative z-10">
                <div className="md:w-1/4 flex-shrink-0">
                  <img 
                    src="/images/willtheyconvertavatar.jpg" 
                    alt="WillTheyConvert Validation Expert" 
                    className="rounded-full shadow-md border-4 border-white mx-auto max-w-full"
                    width={200}
                    height={200}
                  />
                </div>
                <div className="flex-1">
                  <p className="text-lg text-gray-700 mb-6">
                    Fake door testing isn't just a clever hack—it's a proven method used by some of the most successful startups in tech history.
                  </p>
                  <ul className="space-y-4 mb-6">
                    <li className="flex items-start gap-2">
                      <CheckCircle2 className="h-5 w-5 text-indigo-600 flex-shrink-0 mt-0.5" />
                      <p>
                        <a href="https://www.airbnb.com" target="_blank" rel="noopener noreferrer" className="font-semibold text-indigo-600 hover:text-violet-600 hover:underline transition-colors">Airbnb</a> used a basic landing page to test if people would actually book rooms in strangers' homes before building their full platform.
                      </p>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle2 className="h-5 w-5 text-indigo-600 flex-shrink-0 mt-0.5" />
                      <p>
                        <a href="https://www.dropbox.com" target="_blank" rel="noopener noreferrer" className="font-semibold text-indigo-600 hover:text-violet-600 hover:underline transition-colors">Dropbox</a> launched with a simple explainer video to measure interest before writing the complex sync technology behind the scenes.
                      </p>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle2 className="h-5 w-5 text-indigo-600 flex-shrink-0 mt-0.5" />
                      <p>
                        <a href="https://www.groupon.com" target="_blank" rel="noopener noreferrer" className="font-semibold text-indigo-600 hover:text-violet-600 hover:underline transition-colors">Groupon</a> started as a WordPress blog, manually sending deals to see if there was any real demand.
                      </p>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle2 className="h-5 w-5 text-indigo-600 flex-shrink-0 mt-0.5" />
                      <p>
                        <a href="https://www.producthunt.com" target="_blank" rel="noopener noreferrer" className="font-semibold text-indigo-600 hover:text-violet-600 hover:underline transition-colors">Product Hunt</a> began as an email list to test whether people were interested in discovering new products daily.
                      </p>
                    </li>
                  </ul>
                  <p className="text-lg font-medium text-gray-900">
                    These companies didn't wait to build. They validated their ideas first—with fake doors—and only built once they knew people cared.
                  </p>
                </div>
              </div>
            </div>
          </section>
          
          <section id="how-it-works" className="space-y-8">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-indigo-500 to-violet-600 flex items-center justify-center">
                <Target className="h-6 w-6 text-white" />
              </div>
              <h2 className="text-3xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-indigo-600 to-violet-600">How Our Platform Works</h2>
            </div>

            <div className="relative">
              <div className="absolute left-16 top-12 bottom-12 w-1 bg-gradient-to-b from-indigo-300 to-violet-300 hidden md:block"></div>
              
              <div className="space-y-12">
                {[
                  {
                    number: "01",
                    title: "Create Your Test",
                    description: "Design a landing page for your idea in minutes using our drag-and-drop editor. No coding required. Add simulated signup forms, payment buttons, or feature requests."
                  },
                  {
                    number: "02",
                    title: "Get Targeted Traffic",
                    description: "Share your test link with potential customers. Or use our integrated traffic sources to reach your target audience instantly (optional paid feature)."
                  },
                  {
                    number: "03",
                    title: "Measure Real Interest",
                    description: "Our platform tracks how many people actually click signup, payment, or other key conversion buttons. This shows true intent, not just what people say they'd do."
                  },
                  {
                    number: "04",
                    title: "Make Data-Driven Decisions",
                    description: "Learn if your idea has legs with hard data. Conversion rates under 2% suggest rethinking. Over 5% means you're onto something worth building."
                  }
                ].map((step, i) => (
                  <div key={i} className="relative z-10">
                    <div className="flex flex-col md:flex-row gap-8 items-center">
                      <div className="md:w-1/6 flex justify-center">
                        <div className="w-12 h-12 rounded-full bg-gradient-to-r from-indigo-600 to-violet-600 text-white flex items-center justify-center font-bold text-lg relative z-20 shadow-lg">
                          {step.number}
                        </div>
                      </div>
                      <div className="md:w-4/5">
                        <h3 className="text-2xl font-bold mb-2">{step.title}</h3>
                        <p className="text-muted-foreground">{step.description}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            
            <div className="bg-gradient-to-r from-indigo-50 to-violet-50 rounded-2xl p-8 border border-indigo-200 mt-12 shadow-lg">
              <div className="flex flex-col md:flex-row gap-8 items-center">
                <div className="flex-1">
                  <h3 className="text-2xl font-bold mb-3">Ready to validate your idea?</h3>
                  <p className="text-lg mb-6">
                    Create your free test in minutes and get real market feedback before investing in development.
                  </p>
                  <Link to="/create" className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-indigo-600 to-violet-600 hover:from-indigo-700 hover:to-violet-700 text-white rounded-full transition-all font-medium shadow-md hover:shadow-lg transform hover:scale-105">
                    Start Free Validation
                    <ArrowRight className="h-4 w-4" />
                  </Link>
                </div>
                <div className="md:w-1/3 flex-shrink-0 bg-white p-5 rounded-xl shadow-md border border-indigo-100">
                  <div className="text-center">
                    <p className="text-sm font-medium text-muted-foreground mb-1">Average Time to First Results</p>
                    <p className="text-4xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-indigo-600 to-violet-600">24 hours</p>
                    <div className="flex items-center justify-center gap-1 mt-2">
                      <Clock className="h-4 w-4 text-muted-foreground" />
                      <p className="text-sm text-muted-foreground">Setup takes just 8 minutes</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>
          
          <section className="space-y-8">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-indigo-500 to-violet-600 flex items-center justify-center">
                <AlertTriangle className="h-6 w-6 text-white" />
              </div>
              <h2 className="text-3xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-indigo-600 to-violet-600">Validation FAQ</h2>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {[
                {
                  question: "What types of ideas can I validate?",
                  answer: "Our platform works for SaaS, mobile apps, physical products, services, marketplaces, and most other business models. If you need customers to convert in some way, we can help test it."
                },
                {
                  question: "Isn't this just a landing page builder?",
                  answer: "No. Unlike traditional landing pages, our platform simulates the full user journey with fake doors that measure real intent. We also provide analytics specifically designed for idea validation."
                },
                {
                  question: "What if I deceive users testing my idea?",
                  answer: "We follow ethical validation practices. The platform clearly labels tests as 'coming soon' and users can opt-in to be notified when your product launches. No payment info is collected."
                },
                {
                  question: "How accurate are the results?",
                  answer: "Validation tests show strong correlation with actual market performance. While no testing is 100% accurate, this approach has been proven to significantly increase startup success rates."
                }
              ].map((faq, i) => (
                <div key={i} className="bg-white rounded-2xl p-6 shadow-lg border border-gray-200 hover:border-indigo-300 hover:shadow-xl transition-all">
                  <h3 className="text-xl font-bold mb-3">{faq.question}</h3>
                  <p className="text-muted-foreground">{faq.answer}</p>
                </div>
              ))}
            </div>
          </section>
          
          {/* Final CTA */}
          <section className="bg-gradient-to-r from-indigo-600 to-violet-600 text-white rounded-3xl p-10 shadow-2xl">
            <div className="flex flex-col md:flex-row items-center gap-8">
              <div className="flex-1">
                <h2 className="text-3xl font-bold mb-4">Don't guess if they'll convert.</h2>
                <h3 className="text-2xl font-light mb-4">Know for sure.</h3>
                <p className="text-lg mb-6 text-indigo-100">
                  Create your first validation test today and find out if real users want what you're building.
                </p>
                <div className="flex gap-4 flex-wrap">
                  <Link to="/create" className="inline-flex items-center gap-2 px-6 py-3 bg-white text-indigo-600 rounded-full hover:bg-white/90 transition-all text-lg font-medium shadow-md hover:shadow-lg transform hover:scale-105">
                    Start Your Free Test
                    <ArrowRight className="h-4 w-4" />
                  </Link>
                  <Link to="/case-studies" className="inline-flex items-center gap-2 px-6 py-3 bg-transparent text-white border-2 border-white/30 rounded-full hover:bg-white/10 transition-all text-lg font-medium">
                    See Case Studies
                  </Link>
                </div>
              </div>
              <div className="md:w-1/3 bg-white/10 backdrop-blur-sm p-5 rounded-2xl shadow-inner border border-white/20">
                <div className="flex flex-col items-center text-center">
                  <LineChart className="h-12 w-12 mb-3 text-white/80" />
                  <div className="text-5xl font-bold mb-2">3x</div>
                  <p className="text-lg font-medium">Higher chance of success</p>
                  <Separator className="w-16 mx-auto my-3 bg-white/20" />
                  <p className="text-sm text-white/80">Based on analysis of 1,000+ startups</p>
                </div>
              </div>
            </div>
          </section>
        </div>
      </div>
    </>
  );
};

export default ValidateStartup; 